import React, { useEffect, useMemo, useState } from 'react';
import { listUsersForAdmin } from '../lib/users.js';
import './UsersPage.css';

// ============================================================
// UsersPage — Session 3 (Phase 2).
//
// Read-only list of /users with client-side search over the loaded page,
// cursor-based pagination, and a read-only detail drawer on row click.
// No writes this session — AGP assignment / ban / suspend actions belong
// to future sessions (AGP subsystem, moderation subsystem respectively).
//
// COLUMN SET (locked with Rajasekhar): User / Phone / City / Joined
//   - Email column dropped: email doesn't live in /users doc, only in
//     Firebase Auth. Backfilling email to /users at sign-in was proposed
//     as Option 1 but deferred to a future session so we can ship the
//     Users page today.
//   - AGP Status / AGP ID / # Biz columns dropped: no user doc has these
//     fields yet. They land in Session 4 alongside the AGP subsystem.
//
// FIRESTORE COST PER SESSION (typical admin use):
//   Page mount:      25 reads
//   Load more:       25 reads per click
//   Search typing:   0 (client-side filter over loaded page)
//   Row → detail:    0 (data already in memory)
// ============================================================

// Client-side search matcher — case-insensitive substring across the
// fields an admin is likely to search by. Loaded page only; no
// additional Firestore query.
function matchesSearch(user, term) {
  if (!term) return true;
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    user.name,
    user.phone,
    user.currentCity,
    user.profession,
    user.bio,
    user.id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

// Format a Firestore Timestamp / Date / ISO string for display.
// Returns '—' for null/missing/invalid.
function fmtDate(ts) {
  if (!ts) return '—';
  let d;
  if (typeof ts?.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts === 'string') d = new Date(ts);
  else if (typeof ts?.seconds === 'number') d = new Date(ts.seconds * 1000);
  else return '—';
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Human-readable rendering for a field value in the detail drawer.
// Same generic renderer as BusinessesPage — handles Timestamps, arrays,
// booleans, maps, and primitives. Uses JSON.stringify for nested objects.
function renderFieldValue(v) {
  if (v == null || v === '') return <span className="sa-detail-empty">—</span>;
  if (typeof v?.toDate === 'function') return fmtDate(v);
  if (typeof v?.seconds === 'number' && typeof v?.nanoseconds === 'number') return fmtDate(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.length ? v.join(', ') : <span className="sa-detail-empty">—</span>;
  if (typeof v === 'object') return <pre className="sa-detail-json">{JSON.stringify(v, null, 2)}</pre>;
  if (typeof v === 'string' && v.startsWith('http')) {
    return (
      <a href={v} target="_blank" rel="noopener noreferrer" className="sa-detail-link">
        {v.length > 60 ? v.slice(0, 60) + '…' : v}
      </a>
    );
  }
  return String(v);
}

// Compute initials for the avatar. Prefer explicit user.initials field
// (Aangan writes this at onboarding), otherwise derive from name.
function initialsFor(user) {
  if (user.initials) return user.initials.slice(0, 2).toUpperCase();
  const name = user.name || '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const chars = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return chars || '?';
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  // Load first page on mount. No filter dependency this session, so a
  // single effect handles the initial fetch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listUsersForAdmin({ cursor: null })
      .then((res) => {
        if (cancelled) return;
        setUsers(res.users);
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[UsersPage] initial load failed:', err);
        setError(err?.message || 'Failed to load users');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore || !lastDoc) return;
    setLoadingMore(true);
    try {
      const res = await listUsersForAdmin({ cursor: lastDoc });
      setUsers((prev) => [...prev, ...res.users]);
      setLastDoc(res.lastDoc);
      setHasMore(res.hasMore);
    } catch (err) {
      console.error('[UsersPage] load-more failed:', err);
      setError(err?.message || 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  // Client-side search over the loaded page. Memoized so typing doesn't
  // re-filter unless the search term or loaded list actually changed.
  const visibleUsers = useMemo(
    () => users.filter((u) => matchesSearch(u, search)),
    [users, search],
  );

  return (
    <>
      <div className="sa-filter-bar">
        {/* No filter chips this session — filters land when the AGP
            subsystem (Session 4) introduces user-status semantics. */}
        <input
          type="text"
          className="sa-filter-search sa-filter-search-wide"
          placeholder="Search by name, phone, city, profession…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && (
        <div className="sa-error-banner">
          <b>Couldn't load users.</b> {error}
        </div>
      )}

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Phone</th>
              <th>City</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="sa-table-empty">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={4} className="sa-table-empty">
                  {search
                    ? `No results for "${search}" on this page. Try Load more or a different query.`
                    : users.length === 0
                      ? 'No users found.'
                      : 'No results.'}
                </td>
              </tr>
            )}

            {!loading &&
              visibleUsers.map((user) => (
                <tr key={user.id} onClick={() => setSelectedUser(user)}>
                  <td>
                    <div className="sa-user-cell">
                      <UserAvatar user={user} />
                      <div className="sa-user-cell-text">
                        <div className="sa-user-cell-primary">{user.name || '—'}</div>
                        {user.profession && (
                          <div className="sa-user-cell-secondary">{user.profession}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{user.phone || <span className="sa-detail-empty">—</span>}</td>
                  <td>{user.currentCity || <span className="sa-detail-empty">—</span>}</td>
                  <td>{fmtDate(user.createdAt)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {!loading && hasMore && (
        <div className="sa-load-more-wrap">
          <button className="sa-btn" onClick={handleLoadMore} disabled={loadingMore} type="button">
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {selectedUser && (
        <UserDetailDrawer user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </>
  );
}

// ============================================================
// UserAvatar — photoUrl if present, otherwise initials with user's
// stored color. Same pattern V1 uses for member avatars.
// ============================================================
function UserAvatar({ user }) {
  const initials = initialsFor(user);
  const bg = user.color || 'var(--sa-accent)';

  if (user.photoUrl) {
    return (
      <div className="sa-user-avatar sa-user-avatar-photo">
        <img
          src={user.photoUrl}
          alt={user.name || 'User'}
          onError={(e) => {
            // If photo fails to load, hide the img so the initials
            // fallback shows through the parent bg.
            e.target.style.display = 'none';
          }}
        />
      </div>
    );
  }

  return (
    <div className="sa-user-avatar" style={{ background: bg }}>
      {initials}
    </div>
  );
}

// ============================================================
// UserDetailDrawer — read-only side panel showing every field on the
// user doc. Deliberately generic (iterates doc keys) so schema additions
// show up automatically without touching this component.
// ============================================================

// Field ordering for the detail drawer: known-important fields first
// in a stable order, then everything else alphabetical. Same pattern as
// BusinessesPage BusinessDetailDrawer.
const DETAIL_TOP_FIELDS = [
  'name', 'initials', 'phone', 'currentCity', 'profession', 'bio',
  'createdAt', 'onboardedAt', 'celebratedAt', 'scheduledForDeletionAt',
  'photoUrl', 'color', 'status', 'memberOfGroupIds', 'blockedUserIds',
  'superAdmin', 'isPlatformAdmin',
];

function UserDetailDrawer({ user, onClose }) {
  const allKeys = Object.keys(user).filter((k) => k !== 'id');
  const topKeys = DETAIL_TOP_FIELDS.filter((k) => allKeys.includes(k));
  const restKeys = allKeys.filter((k) => !DETAIL_TOP_FIELDS.includes(k)).sort();
  const orderedKeys = [...topKeys, ...restKeys];

  return (
    <>
      <div className="sa-drawer-backdrop" onClick={onClose} />
      <div className="sa-detail-panel open" role="dialog" aria-modal="true">
        <div className="sa-detail-panel-header">
          <UserAvatar user={user} />
          <div className="sa-detail-panel-title-block">
            <div className="sa-detail-panel-title">{user.name || 'User'}</div>
            <div className="sa-detail-panel-sub">UID {user.id}</div>
          </div>
          <button
            className="sa-detail-panel-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>

        <div className="sa-detail-panel-body">
          <div className="sa-detail-block">
            <div className="sa-detail-block-title">All fields</div>
            {orderedKeys.map((key) => (
              <div className="sa-detail-row" key={key}>
                <div className="sa-detail-row-label">{key}</div>
                <div className="sa-detail-row-value">{renderFieldValue(user[key])}</div>
              </div>
            ))}
          </div>

          <div className="sa-detail-hint">
            Read-only view. AGP assignment, ban / suspend, and moderation actions land in future sessions.
          </div>
        </div>
      </div>
    </>
  );
}
